import React from 'react';
import { useTranslation } from 'react-i18next';
import './ReportDialog.css';

/**
 * Dialog zur Auswahl der Report-Inhalte (Vorderseite, Tätigkeiten, Bilanzen, Orientierung)
 * und Aktionen Drucken / PDF exportieren.
 */
function ReportDialog({
  open,
  onClose,
  reportOptions,
  setReportOptions,
  isReportPreparing,
  printPreviewReady,
  isPrintInProgress,
  onPrint,
  onExportPDF,
  onPrintTrigger,
  reportPrintOrientationRef,
  isPdfExportForPreviewRef
}) {
  const { t } = useTranslation();
  if (!open) return null;

  const hasAnyOption = reportOptions.vorderseite ||
    reportOptions.messpunkteDetail ||
    reportOptions.messpunkteKompakt ||
    reportOptions.messpunkteBilanzZeitarten ||
    reportOptions.bilanzKuerzel;

  const optionsDisabled = !hasAnyOption;
  const optionsTitle = isPrintInProgress ? t('dialog.report_dialog.please_wait') : optionsDisabled
    ? t('dialog.report_dialog.select_content')
    : '';

  return (
    <div className="edit-overlay" onClick={onClose}>
      <div
        className="edit-modal report-dialog-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '600px', position: 'relative' }}
      >
        {isReportPreparing && (
          <div
            className="report-preparing-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              borderRadius: '8px',
              zIndex: 10
            }}
          >
            <div
              className="report-preparing-spinner"
              style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e5e7eb',
                borderTopColor: '#667eea',
                borderRadius: '50%',
                animation: 'report-spin 0.8s linear infinite'
              }}
            />
            <span style={{ fontSize: '1rem', fontWeight: '500', color: '#374151' }}>{t('dialog.report_dialog.loading')}</span>
          </div>
        )}
        <div className="edit-header">
          <h3>📄 {t('dialog.report_dialog.title')}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="edit-content">
          <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6', marginBottom: '20px' }}>
            {t('dialog.report_dialog.intro')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={reportOptions.vorderseite}
                onChange={(e) => setReportOptions({ ...reportOptions, vorderseite: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500' }}>{t('dialog.report_dialog.vorderseite')}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setReportOptions({ ...reportOptions, messpunkteDetail: false, messpunkteKompakt: false });
                  } else {
                    setReportOptions({ ...reportOptions, messpunkteDetail: true, messpunkteKompakt: false });
                  }
                }}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500' }}>{t('dialog.report_dialog.taetigkeiten')}</span>
            </label>
            <div style={{ marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="taetigkeiten-view"
                  checked={reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt}
                  disabled={!reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setReportOptions({ ...reportOptions, messpunkteDetail: true, messpunkteKompakt: false });
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.9rem', color: (reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt) ? '#374151' : '#9ca3af' }}>{t('dialog.report_dialog.detail')}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="taetigkeiten-view"
                  checked={reportOptions.messpunkteKompakt}
                  disabled={!reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setReportOptions({ ...reportOptions, messpunkteDetail: false, messpunkteKompakt: true });
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.9rem', color: (reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt) ? '#374151' : '#9ca3af' }}>{t('dialog.report_dialog.kompakt')}</span>
              </label>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={reportOptions.messpunkteBilanzZeitarten}
                onChange={(e) => setReportOptions({ ...reportOptions, messpunkteBilanzZeitarten: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500' }}>{t('dialog.report_dialog.bilanz_zeitarten')}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={reportOptions.bilanzKuerzel}
                onChange={(e) => setReportOptions({ ...reportOptions, bilanzKuerzel: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '1rem', fontWeight: '500' }}>{t('dialog.report_dialog.bilanz_kuerzel')}</span>
            </label>
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '1rem', fontWeight: '500', display: 'block', marginBottom: '8px' }}>{t('dialog.report_dialog.page_format')}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="report-print-orientation"
                    checked={reportOptions.reportPrintOrientation !== 'landscape'}
                    onChange={() => {
                      if (reportPrintOrientationRef?.current !== undefined) reportPrintOrientationRef.current = 'portrait';
                      setReportOptions((prev) => ({ ...prev, reportPrintOrientation: 'portrait', orientation: 'portrait' }));
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1rem' }}>{t('dialog.report_dialog.portrait')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="report-print-orientation"
                    checked={reportOptions.reportPrintOrientation === 'landscape'}
                    onChange={() => {
                      if (reportPrintOrientationRef?.current !== undefined) reportPrintOrientationRef.current = 'landscape';
                      setReportOptions((prev) => ({ ...prev, reportPrintOrientation: 'landscape', orientation: 'landscape' }));
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1rem' }}>{t('dialog.report_dialog.landscape')}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="edit-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            {t('dialog.cancel')}
          </button>
          {printPreviewReady ? (
            <>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '10px' }}>
                {t('dialog.report_dialog.preview_ready')}
              </p>
              <button
                className="btn btn-primary"
                onClick={onPrintTrigger}
                title={t('dialog.report_dialog.open_print_preview_title')}
              >
                {isPdfExportForPreviewRef?.current ? `📄 ${t('dialog.report_dialog.open_pdf_preview')}` : `🖨️ ${t('dialog.report_dialog.open_print_preview')}`}
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-primary"
                onClick={onPrint}
                disabled={isPrintInProgress || optionsDisabled}
                title={optionsTitle}
              >
                {isPrintInProgress ? `⏳ ${t('dialog.report_dialog.please_wait')}` : `🖨️ ${t('dialog.report_dialog.print')}`}
              </button>
              <button
                className="btn btn-primary"
                onClick={onExportPDF}
                disabled={isPrintInProgress || optionsDisabled}
                title={optionsTitle}
              >
                {isPrintInProgress ? `⏳ ${t('dialog.report_dialog.please_wait')}` : `📄 ${t('dialog.report_dialog.export_pdf')}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportDialog;
